import {
  MESSAGE_ATTACHMENT_EXTENSIONS,
  MESSAGE_ATTACHMENT_FORMATS_LABEL,
  formatFileSize,
  type MessageAttachmentLimits,
} from '@easyhelp/shared';

const ALLOWED_EXTENSIONS = new Set(Object.values(MESSAGE_ATTACHMENT_EXTENSIONS).flat());

/** Estensione in minuscolo, senza punto; vuota se il nome non ne ha. */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Problemi degli allegati scelti nel composer, rispetto ai limiti dell'istanza (DEC-008): numero di
 * file, dimensione di ciascuno e del totale, formato dall'estensione. È un aiuto per l'operatore,
 * non una garanzia: l'API verifica il tipo reale dal contenuto e resta la fonte di verità.
 * @returns un messaggio per problema, vuoto se si può inviare
 */
export function attachmentProblems(
  files: readonly Pick<File, 'name' | 'size'>[],
  limits: MessageAttachmentLimits
): string[] {
  const problems: string[] = [];
  if (files.length > limits.maxFiles) {
    problems.push(
      `Al più ${limits.maxFiles} allegati per messaggio: ne hai scelti ${files.length}, togline ${files.length - limits.maxFiles}.`
    );
  }
  for (const file of files) {
    if (!ALLOWED_EXTENSIONS.has(extensionOf(file.name))) {
      problems.push(`«${file.name}» non è tra i formati ammessi: ${MESSAGE_ATTACHMENT_FORMATS_LABEL}.`);
    } else if (file.size === 0) {
      problems.push(`«${file.name}» è vuoto.`);
    } else if (file.size > limits.maxFileBytes) {
      problems.push(
        `«${file.name}» pesa ${formatFileSize(file.size)}: il limite è ${formatFileSize(limits.maxFileBytes)} per file.`
      );
    }
  }
  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (total > limits.maxMessageBytes) {
    problems.push(
      `Gli allegati insieme pesano ${formatFileSize(total)}: il limite è ${formatFileSize(limits.maxMessageBytes)} per messaggio.`
    );
  }
  return problems;
}

/** Stesso file scelto due volte (nome, dimensione e data): non si allega due volte. */
export function sameFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}
